import { css } from '@emotion/react';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { approvalListStyles } from '@/components/Approval/@styles/listStyles';
import { APPROVAL_SECTION_GAP_LG } from '@/components/Approval/@components/InfoSection';

/** 마크다운 렌더 결과 wrapper 클래스 — HTML 폴백에는 없는 마크다운 전용 스타일의 scope */
export const MARKDOWN_CLASS = 'guide-markdown';
export const TABLE_SCROLL_CLASS = 'guide-table-scroll';
/** 폭이 % 이거나 없는 표 — 작성 화면 너비를 채움 */
export const TABLE_FLUID_CLASS = 'guide-table-fluid';
const GUIDE_TABLE_MIN_WIDTH = 760;

/**
 * 양식 가이드 wrapper — HTML 폴백·마크다운 렌더 양쪽 공통.
 * 표는 element selector 로 일괄 처리 — 레거시 HTML 의 table-bordered 등 Bootstrap 류 클래스는
 * 포탈에 정의가 없어 클래스 의존 시 테두리·머리행이 통째로 사라짐.
 */
export const GuideTemplateRoot = styled(Box)(
  () => css`
    margin-bottom: ${APPROVAL_SECTION_GAP_LG}px;
    overflow-wrap: break-word;

    /* 포탈 글로벌엔 ul/ol 초기화가 없음 — 가이드 HTML 안에서만 browser default 제거 */
    & ul,
    & ol {
      list-style: none;
      padding-left: 0;
    }

    ${approvalListStyles}

    & img {
      max-width: 100%;
    }

    & .${TABLE_SCROLL_CLASS} {
      max-width: 100%;
      margin: 0 0 16px;
      overflow-x: auto;
    }
    & table {
      width: max-content;
      max-width: 100%;
      border-collapse: collapse;
    }
    /* 좁은 화면에서도 표는 최소 폭까지만 줄고 나머지는 스크롤 — 더 줄이면 셀 글자가 한 자씩 쪼개짐 */
    /* 에디터 표 클래스의 display: block 이 붙으면 표 너비가 칸에 안 먹음 */
    & .${TABLE_SCROLL_CLASS} > table {
      display: table;
      max-width: max(100%, ${GUIDE_TABLE_MIN_WIDTH}px);
    }
    & .${TABLE_SCROLL_CLASS} > table.${TABLE_FLUID_CLASS} {
      width: 100%;
      min-width: ${GUIDE_TABLE_MIN_WIDTH}px;
    }
    /* HTML 가이드 셀 여백은 어드민 편집기와 같아야 함 — 더 넓으면 같은 열 비율에서 줄이 넘침 */
    & table th,
    & table td {
      border: 1px solid var(--dk-line-strong, #d1d9e0);
      padding: 2px 4px;
      vertical-align: top;
      word-break: keep-all;
    }
    & .${MARKDOWN_CLASS} table th,
    & .${MARKDOWN_CLASS} table td {
      padding: 6px 13px;
    }
    & table th {
      background-color: var(--dk-bg-subtle, #f6f8fa);
      font-weight: 600;
    }
    & table th > :last-child,
    & table td > :last-child {
      margin-bottom: 0;
    }

    /* 이하 마크다운 전용. 위 ul/ol 초기화를 되돌려 marker 복원 */
    & .${MARKDOWN_CLASS} > :last-child {
      margin-bottom: 0;
    }
    & .${MARKDOWN_CLASS} p {
      margin: 0 0 16px;
    }
    & .${MARKDOWN_CLASS} h1,
    & .${MARKDOWN_CLASS} h2,
    & .${MARKDOWN_CLASS} h3,
    & .${MARKDOWN_CLASS} h4,
    & .${MARKDOWN_CLASS} h5,
    & .${MARKDOWN_CLASS} h6 {
      margin: 0 0 16px;
      font-weight: 600;
      line-height: 1.25;
    }
    & .${MARKDOWN_CLASS} * + h1,
    & .${MARKDOWN_CLASS} * + h2,
    & .${MARKDOWN_CLASS} * + h3,
    & .${MARKDOWN_CLASS} * + h4,
    & .${MARKDOWN_CLASS} * + h5,
    & .${MARKDOWN_CLASS} * + h6 {
      margin-top: 24px;
    }
    /* 가이드는 본문보다 위계가 낮음 — h1 은 본문 글자 크기(16px) 와 동일, h3 이하는 굵기로만 구분 */
    & .${MARKDOWN_CLASS} h1 {
      font-size: 16px;
    }
    & .${MARKDOWN_CLASS} h2 {
      font-size: 15px;
    }
    & .${MARKDOWN_CLASS} h3,
    & .${MARKDOWN_CLASS} h4,
    & .${MARKDOWN_CLASS} h5,
    & .${MARKDOWN_CLASS} h6 {
      font-size: 1em;
    }

    & .${MARKDOWN_CLASS} ul,
    & .${MARKDOWN_CLASS} ol {
      margin: 0 0 16px;
      padding-left: 2em;
    }
    & .${MARKDOWN_CLASS} ul {
      list-style: disc;
    }
    & .${MARKDOWN_CLASS} ul ul {
      list-style: circle;
    }
    & .${MARKDOWN_CLASS} ul ul ul {
      list-style: square;
    }
    & .${MARKDOWN_CLASS} ol {
      list-style: decimal;
    }
    & .${MARKDOWN_CLASS} li + li {
      margin-top: 0.25em;
    }
    & .${MARKDOWN_CLASS} li > ul,
    & .${MARKDOWN_CLASS} li > ol {
      margin: 0.25em 0 0;
    }

    & .${MARKDOWN_CLASS} strong {
      font-weight: 600;
    }
    & .${MARKDOWN_CLASS} a {
      color: var(--dk-accent, #0066cc);
      text-decoration: underline;
      word-break: break-all;
    }
    & .${MARKDOWN_CLASS} a:hover {
      color: var(--dk-accent, #004c99);
    }

    & .${MARKDOWN_CLASS} blockquote {
      margin: 0 0 16px;
      padding: 0 1em;
      border-left: 0.25em solid var(--dk-line-strong, #d1d9e0);
      color: var(--dk-text-strong, #59636e);
    }
    & .${MARKDOWN_CLASS} hr {
      height: 1px;
      margin: 24px 0;
      padding: 0;
      border: 0;
      background-color: var(--dk-bg-strong, #d1d9e0);
    }

    & .${MARKDOWN_CLASS} code {
      padding: 0.2em 0.4em;
      background-color: rgba(129, 139, 152, 0.12);
      border-radius: 6px;
      font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo,
        monospace;
      font-size: 0.85em;
    }
    & .${MARKDOWN_CLASS} pre {
      margin: 0 0 16px;
      padding: 16px;
      background-color: rgba(129, 139, 152, 0.12);
      border-radius: 6px;
      line-height: 1.45;
      font-size: 0.85em;
      overflow-x: auto;
    }
    & .${MARKDOWN_CLASS} pre code {
      padding: 0;
      background: none;
      font-size: 1em;
    }

    /* GFM 열 정렬은 인라인 style 로 오므로 기본값만 좌측 */
    & .${MARKDOWN_CLASS} th,
    & .${MARKDOWN_CLASS} td {
      text-align: left;
    }
  `,
);
